import { sourceLabel } from '@/features/imports/ReviewImport';
import { Check, ChevronsRight, EyeOff } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { runStepAction } from './stepEvents';
import { useAcceptImport } from '@/hooks/useImports';
import { useSetupAction } from '@/hooks/useSetup';
import { cn } from '@/lib/utils';
import type { SetupState, SetupStepId } from '@/types';
import {
  LATER_STEPS,
  describeCounts,
  doneCount,
  laterDone,
  stepSource,
  type StepConfig, focusTourTarget } from './steps';

export interface SetupRailProps {
  state: SetupState;
  steps: StepConfig[];
  /** The step shown as a card. Undefined once everything is done. */
  current: StepConfig | undefined;
  onPick: (id: SetupStepId | null) => void;
  onCollapse: () => void;
}

/**
 * The 300px aside on the right. The shell places it as a flex sibling of the
 * page; everything else in the guide (chip, sheet, coach marks) is fixed and
 * does not care where it is mounted.
 */
export function SetupRail({ state, steps, current, onPick, onCollapse }: SetupRailProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const action = useSetupAction();
  const accept = useAcceptImport();
  const done = doneCount(steps, state);
  const total = steps.length;
  const pending = state.pendingImport;

  // Skipping marks the step done; the rail moves on to the next open one.
  function skip(step: StepConfig) {
    action.mutate({ action: 'skipStep', step: step.id });
    const i = steps.indexOf(step);
    const next = steps.slice(i + 1).find((s) => !s.isDone(state));
    onPick(next?.id ?? null);
  }

  return (
    <aside
      aria-label="Setup"
      className="hidden w-[300px] shrink-0 flex-col border-l border-[var(--line)] bg-[var(--surface)] lg:flex"
    >
      <div className="flex items-center justify-between gap-2 border-b border-[var(--line)] px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold tracking-tight">Setup</h2>
          <p className="tnum text-xs text-muted">
            {done} of {total} done
          </p>
        </div>
        <Button
          variant="ghost"
          size="iconSm"
          onClick={onCollapse}
          aria-label="Collapse setup"
          title="Collapse"
        >
          <ChevronsRight />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3">
        <ol className="space-y-1">
          {steps.map((step, i) => {
            const isDone = step.isDone(state);
            const isCurrent = current?.id === step.id;
            const n = i + 1;
            const pendingCard =
              step.id === 'import' && pending ? (
                <PendingImportCard
                  source={pending.source}
                  counts={pending.counts}
                  onReview={() => navigate(`/timesheet?review=${pending.batchId}`)}
                  onAccept={() => accept.mutate({ id: pending.batchId, mode: 'all' })}
                  accepting={accept.isPending}
                />
              ) : null;
            if (isCurrent) {
              return (
                <li key={step.id}>
                  <div className="rounded-xl border border-[#ddd6fe] bg-[#f5f3ff] p-3 dark:border-[var(--line-strong)] dark:bg-[var(--accent-soft)]">
                    <p className="text-xs font-medium text-muted">Step {n}</p>
                    <p className="mt-0.5 text-sm font-semibold">{step.title}</p>
                    <p className="mt-1 text-sm text-muted">{step.body}</p>
                    <div className="mt-3 flex items-center gap-2">
                      {step.cta.to && pathname === step.route ? (
                        <Button variant="primary" size="sm" onClick={() => runStepAction(step.id) || focusTourTarget(step.target)}>
                          {step.cta.here ?? step.cta.label}
                        </Button>
                      ) : step.cta.to ? (
                        <Button variant="primary" size="sm" asChild>
                          <Link to={step.cta.to}>{step.cta.label}</Link>
                        </Button>
                      ) : (
                        <Button variant="primary" size="sm" asChild>
                          <Link to={step.route}>Show me</Link>
                        </Button>
                      )}
                      {!isDone ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => skip(step)}
                          disabled={action.isPending}
                        >
                          Skip
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  {pendingCard}
                </li>
              );
            }
            return (
              <li key={step.id}>
                <button
                  type="button"
                  onClick={() => onPick(step.id)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-[var(--surface-hover)]',
                    isDone ? 'text-muted' : 'text-[var(--text)]'
                  )}
                >
                  <span
                    className={cn(
                      'tnum flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-medium',
                      isDone
                        ? 'bg-[var(--accent)] text-[var(--accent-fg)]'
                        : 'border border-[var(--line-strong)] text-muted'
                    )}
                  >
                    {isDone ? <Check className="size-3" /> : n}
                  </span>
                  <span className={cn('min-w-0 flex-1 truncate', isDone && 'line-through')}>
                    {step.title}
                  </span>
                  {isDone && stepSource(state, step.id) === 'imported' ? (
                    <span className="text-[11px] text-subtle">imported</span>
                  ) : null}
                </button>
                {pendingCard}
              </li>
            );
          })}
        </ol>

        {state.isAdmin ? (
          <div className="mt-5">
            <p className="px-2 text-xs font-medium uppercase tracking-wide text-subtle">
              When you're ready
            </p>
            <ul className="mt-1 space-y-1">
              {LATER_STEPS.map((later) => {
                const isDone = laterDone(state, later.id);
                return (
                  <li key={later.id}>
                    <Link
                      to={later.to}
                      className="flex items-center gap-3 rounded-lg px-2 py-2 text-sm transition-colors hover:bg-[var(--surface-hover)]"
                    >
                      <span
                        className={cn(
                          'flex size-5 shrink-0 items-center justify-center rounded-full',
                          isDone
                            ? 'bg-[var(--accent)] text-[var(--accent-fg)]'
                            : 'border border-dashed border-[var(--line-strong)]'
                        )}
                      >
                        {isDone ? <Check className="size-3" /> : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={cn('block truncate', isDone && 'text-muted line-through')}>
                          {later.title}
                        </span>
                        {!isDone ? (
                          <span className="block truncate text-xs text-muted">{later.body}</span>
                        ) : null}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>

      <div className="border-t border-[var(--line)] px-2 py-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted"
          onClick={() => action.mutate('hide')}
          disabled={action.isPending}
        >
          <EyeOff />
          Hide setup
        </Button>
      </div>
    </aside>
  );
}

function PendingImportCard({
  source,
  counts,
  onReview,
  onAccept,
  accepting
}: {
  source: string;
  counts: Record<string, number>;
  onReview: () => void;
  onAccept: () => void;
  accepting: boolean;
}) {
  const summary = describeCounts(counts);
  return (
    <div className="mt-1 ml-2 rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/40">
      <p className="text-sm font-medium">{sourceLabel(source)} import waiting</p>
      <p className="mt-0.5 text-xs text-muted">
        {summary || 'Nothing counts until you accept it.'}
      </p>
      <div className="mt-2 flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={onReview}>
          Review
        </Button>
        <Button variant="primary" size="sm" onClick={onAccept} loading={accepting}>
          Accept all
        </Button>
      </div>
    </div>
  );
}
